import { formatNumber } from "@/helpers/common/formatNumber";
import { convertLetters } from "@/utils/letterConversion";
import { gql, useQuery } from "@apollo/client";
import { Trophy } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

const STREAM_TEAM_LEADERBOARDS_QUERY = gql`
    query StreamTeamLeaderboardsQuery(
        $runestoneLimit: Int!
        $runestoneOffset: Int!
        $channelRpLimit: Int!
        $channelRpOffset: Int!
        $combinedLimit: Int!
        $combinedOffset: Int!
        $playerRpLimit: Int!
        $playerRpOffset: Int!
    ) {
        streamTeamRunestoneLeaderboard(
            limit: $runestoneLimit
            offset: $runestoneOffset
        ) {
            totalRunestones
            streamTeamMember {
                id
                twitchUsername
                player {
                    id
                    name
                    shield {
                        imageUrl
                        imageWithTagsUrl
                    }
                }
            }
        }
        streamTeamRunestoneLeaderboardCount

        streamTeamChannelRpLeaderboard(
            limit: $channelRpLimit
            offset: $channelRpOffset
        ) {
            totalRavenguardPoints
            streamTeamMember {
                id
                twitchUsername
                player {
                    id
                    name
                    shield {
                        imageUrl
                        imageWithTagsUrl
                    }
                }
            }
        }
        streamTeamChannelRpLeaderboardCount

        streamTeamCombinedRpLeaderboard(
            limit: $combinedLimit
            offset: $combinedOffset
        ) {
            channelRavenguardPoints
            earnedElsewhereRavenguardPoints
            combinedRavenguardPoints
            streamTeamMember {
                id
                twitchUsername
                player {
                    id
                    name
                    shield {
                        imageUrl
                        imageWithTagsUrl
                    }
                }
            }
        }
        streamTeamCombinedRpLeaderboardCount

        streamTeamPlayerRpLeaderboard(
            limit: $playerRpLimit
            offset: $playerRpOffset
        ) {
            totalRavenguardPoints
            player {
                id
                name
                shield {
                    imageUrl
                    imageWithTagsUrl
                }
            }
        }
        streamTeamPlayerRpLeaderboardCount
    }
`;

interface StreamTeamMemberRef {
    id: string;
    twitchUsername?: string | null;
    player: {
        id: string;
        name: string;
        shield?: {
            imageUrl?: string | null;
            imageWithTagsUrl?: string | null;
        } | null;
    };
}

interface RunestoneLeaderboardEntry {
    totalRunestones: number;
    streamTeamMember: StreamTeamMemberRef;
}

interface ChannelRpLeaderboardEntry {
    totalRavenguardPoints: number;
    streamTeamMember: StreamTeamMemberRef;
}

interface CombinedRpLeaderboardEntry {
    channelRavenguardPoints: number;
    earnedElsewhereRavenguardPoints: number;
    combinedRavenguardPoints: number;
    streamTeamMember: StreamTeamMemberRef;
}

interface PlayerRpLeaderboardEntry {
    totalRavenguardPoints: number;
    player: {
        id: string;
        name: string;
        shield?: {
            imageUrl?: string | null;
            imageWithTagsUrl?: string | null;
        } | null;
    };
}

interface StreamTeamLeaderboardsData {
    streamTeamRunestoneLeaderboard: RunestoneLeaderboardEntry[];
    streamTeamRunestoneLeaderboardCount: number;
    streamTeamChannelRpLeaderboard: ChannelRpLeaderboardEntry[];
    streamTeamChannelRpLeaderboardCount: number;
    streamTeamCombinedRpLeaderboard: CombinedRpLeaderboardEntry[];
    streamTeamCombinedRpLeaderboardCount: number;
    streamTeamPlayerRpLeaderboard: PlayerRpLeaderboardEntry[];
    streamTeamPlayerRpLeaderboardCount: number;
}

const RUNESTONE_PAGE_SIZE = 20;
const CHANNEL_RP_PAGE_SIZE = 20;
const COMBINED_PAGE_SIZE = 20;
const PLAYER_RP_PAGE_SIZE = 50;

function PaginationControls({
    page,
    pageSize,
    total,
    onPageChange,
}: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (newPage: number) => void;
}) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return (
        <div className="mt-4 flex items-center justify-between gap-3">
            <button
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 0}
                className="hover:border-brand rounded-md border border-zinc-600 px-3 py-1 text-sm text-zinc-200 transition disabled:cursor-not-allowed disabled:opacity-50"
            >
                Previous
            </button>
            <p className="font-garamond text-sm text-zinc-300">
                Page {page + 1} of {totalPages}
            </p>
            <button
                onClick={() => onPageChange(page + 1)}
                disabled={page + 1 >= totalPages}
                className="hover:border-brand rounded-md border border-zinc-600 px-3 py-1 text-sm text-zinc-200 transition disabled:cursor-not-allowed disabled:opacity-50"
            >
                Next
            </button>
        </div>
    );
}

export default function StreamTeamLeaderboardsPage() {
    const [activeTab, setActiveTab] = useState<
        "runestones" | "channelRp" | "combinedRp" | "players"
    >("runestones");
    const [runestonePage, setRunestonePage] = useState(0);
    const [channelRpPage, setChannelRpPage] = useState(0);
    const [combinedPage, setCombinedPage] = useState(0);
    const [playerRpPage, setPlayerRpPage] = useState(0);

    const variables = useMemo(
        () => ({
            runestoneLimit: RUNESTONE_PAGE_SIZE,
            runestoneOffset: runestonePage * RUNESTONE_PAGE_SIZE,
            channelRpLimit: CHANNEL_RP_PAGE_SIZE,
            channelRpOffset: channelRpPage * CHANNEL_RP_PAGE_SIZE,
            combinedLimit: COMBINED_PAGE_SIZE,
            combinedOffset: combinedPage * COMBINED_PAGE_SIZE,
            playerRpLimit: PLAYER_RP_PAGE_SIZE,
            playerRpOffset: playerRpPage * PLAYER_RP_PAGE_SIZE,
        }),
        [runestonePage, channelRpPage, combinedPage, playerRpPage],
    );

    const { data, loading, error } = useQuery<StreamTeamLeaderboardsData>(
        STREAM_TEAM_LEADERBOARDS_QUERY,
        {
            variables,
            fetchPolicy: "cache-and-network",
        },
    );

    if (loading && !data) {
        return (
            <div className="mx-auto w-full px-4 py-8 sm:px-6 sm:py-12 lg:w-3/4">
                <p className="font-garamond text-zinc-300">
                    Loading leaderboards...
                </p>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="mx-auto w-full px-4 py-8 sm:px-6 sm:py-12 lg:w-3/4">
                <p className="font-garamond text-red-400">
                    Failed to load leaderboards: {error.message}
                </p>
            </div>
        );
    }

    const runestoneRows = data?.streamTeamRunestoneLeaderboard ?? [];
    const channelRpRows = data?.streamTeamChannelRpLeaderboard ?? [];
    const combinedRows = data?.streamTeamCombinedRpLeaderboard ?? [];
    const playerRpRows = data?.streamTeamPlayerRpLeaderboard ?? [];

    const runestoneCount = data?.streamTeamRunestoneLeaderboardCount ?? 0;
    const channelRpCount = data?.streamTeamChannelRpLeaderboardCount ?? 0;
    const combinedCount = data?.streamTeamCombinedRpLeaderboardCount ?? 0;
    const playerRpCount = data?.streamTeamPlayerRpLeaderboardCount ?? 0;

    return (
        <div className="mx-auto w-full px-4 py-8 sm:px-6 sm:py-12 lg:w-3/4">
            <div className="mb-8">
                <h1 className="font-prviking text-brand mb-3 flex items-center gap-2 text-3xl sm:text-4xl lg:text-5xl">
                    <Trophy className="h-8 w-8 sm:h-10 sm:w-10" />
                    {convertLetters("Stream Team Leaderboards")}
                </h1>
                <p className="font-garamond text-zinc-300">
                    Track runestone redemptions and Ravenguard Points across the
                    entire Rookery.
                </p>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <TabButton
                    label="Runestones"
                    isActive={activeTab === "runestones"}
                    onClick={() => setActiveTab("runestones")}
                />
                <TabButton
                    label="Channel RP"
                    isActive={activeTab === "channelRp"}
                    onClick={() => setActiveTab("channelRp")}
                />
                <TabButton
                    label="Combined RP"
                    isActive={activeTab === "combinedRp"}
                    onClick={() => setActiveTab("combinedRp")}
                />
                <TabButton
                    label="Top Players"
                    isActive={activeTab === "players"}
                    onClick={() => setActiveTab("players")}
                />
            </div>

            <section
                className={`border-brand_stream_team rounded-xl border bg-zinc-900/70 p-4 sm:p-6 ${
                    activeTab === "runestones" ? "" : "hidden"
                }`}
            >
                <h2 className="font-prviking text-brand mb-3 text-xl sm:text-2xl">
                    {convertLetters("Runestones Redeemed Per Streamer")}
                </h2>
                <LeaderboardTable
                    headers={["#", "Streamer", "Runestones Redeemed"]}
                    rows={runestoneRows.map((row, index) => {
                        const rank =
                            runestonePage * RUNESTONE_PAGE_SIZE + index + 1;
                        const member = row.streamTeamMember;
                        const label = member.player.name;

                        return [
                            String(rank),
                            <ShieldedNameLink
                                key={`${member.id}-name`}
                                name={label}
                                to={
                                    member.twitchUsername
                                        ? `/team/${member.twitchUsername}`
                                        : undefined
                                }
                                shield={member.player.shield}
                            />,
                            formatNumber(row.totalRunestones || 0),
                        ];
                    })}
                    emptyMessage="No runestone redemption data yet."
                />
                <PaginationControls
                    page={runestonePage}
                    pageSize={RUNESTONE_PAGE_SIZE}
                    total={runestoneCount}
                    onPageChange={setRunestonePage}
                />
            </section>

            <section
                className={`border-brand_stream_team rounded-xl border bg-zinc-900/70 p-4 sm:p-6 ${
                    activeTab === "channelRp" ? "" : "hidden"
                }`}
            >
                <h2 className="font-prviking text-brand mb-3 text-xl sm:text-2xl">
                    {convertLetters(
                        "Ravenguard Points Redeemed On Each Streamer's Channel",
                    )}
                </h2>
                <LeaderboardTable
                    headers={["#", "Streamer", "Channel RP"]}
                    rows={channelRpRows.map((row, index) => {
                        const rank =
                            channelRpPage * CHANNEL_RP_PAGE_SIZE + index + 1;
                        const member = row.streamTeamMember;
                        const label = member.player.name;

                        return [
                            String(rank),
                            <ShieldedNameLink
                                key={`${member.id}-name`}
                                name={label}
                                to={
                                    member.twitchUsername
                                        ? `/team/${member.twitchUsername}`
                                        : undefined
                                }
                                shield={member.player.shield}
                            />,
                            formatNumber(row.totalRavenguardPoints || 0),
                        ];
                    })}
                    emptyMessage="No Ravenguard Point data yet."
                />
                <PaginationControls
                    page={channelRpPage}
                    pageSize={CHANNEL_RP_PAGE_SIZE}
                    total={channelRpCount}
                    onPageChange={setChannelRpPage}
                />
            </section>

            <section
                className={`border-brand_stream_team rounded-xl border bg-zinc-900/70 p-4 sm:p-6 ${
                    activeTab === "combinedRp" ? "" : "hidden"
                }`}
            >
                <h2 className="font-prviking text-brand mb-3 text-xl sm:text-2xl">
                    {convertLetters(
                        "Combined Streamer RP (Channel + Earned Elsewhere)",
                    )}
                </h2>
                <LeaderboardTable
                    headers={[
                        "#",
                        "Streamer",
                        "Channel RP",
                        "Earned Elsewhere",
                        "Combined RP",
                    ]}
                    rows={combinedRows.map((row, index) => {
                        const rank =
                            combinedPage * COMBINED_PAGE_SIZE + index + 1;
                        const member = row.streamTeamMember;
                        const label = member.player.name;

                        return [
                            String(rank),
                            <ShieldedNameLink
                                key={`${member.id}-name`}
                                name={label}
                                to={
                                    member.twitchUsername
                                        ? `/team/${member.twitchUsername}`
                                        : undefined
                                }
                                shield={member.player.shield}
                            />,
                            formatNumber(row.channelRavenguardPoints || 0),
                            formatNumber(
                                row.earnedElsewhereRavenguardPoints || 0,
                            ),
                            formatNumber(row.combinedRavenguardPoints || 0),
                        ];
                    })}
                    emptyMessage="No combined RP data yet."
                />
                <PaginationControls
                    page={combinedPage}
                    pageSize={COMBINED_PAGE_SIZE}
                    total={combinedCount}
                    onPageChange={setCombinedPage}
                />
            </section>

            <section
                className={`border-brand_stream_team rounded-xl border bg-zinc-900/70 p-4 sm:p-6 ${
                    activeTab === "players" ? "" : "hidden"
                }`}
            >
                <h2 className="font-prviking text-brand mb-3 text-xl sm:text-2xl">
                    {convertLetters("Top Players By Total RP")}
                </h2>
                <p className="font-garamond mb-3 text-sm text-zinc-400">
                    Includes any player with more than 0 Ravenguard Points.
                </p>
                <LeaderboardTable
                    headers={["#", "Player", "Total RP"]}
                    rows={playerRpRows.map((row, index) => {
                        const rank =
                            playerRpPage * PLAYER_RP_PAGE_SIZE + index + 1;
                        return [
                            String(rank),
                            <ShieldedNameLink
                                key={`${row.player.id}-name`}
                                name={row.player.name}
                                shield={row.player.shield}
                            />,
                            formatNumber(row.totalRavenguardPoints || 0),
                        ];
                    })}
                    emptyMessage="No player RP data yet."
                />
                <PaginationControls
                    page={playerRpPage}
                    pageSize={PLAYER_RP_PAGE_SIZE}
                    total={playerRpCount}
                    onPageChange={setPlayerRpPage}
                />
            </section>
        </div>
    );
}

function ShieldedNameLink({
    name,
    shield,
    to,
}: {
    name: string;
    shield?: {
        imageUrl?: string | null;
        imageWithTagsUrl?: string | null;
    } | null;
    to?: string;
}) {
    const imageUrl =
        shield?.imageWithTagsUrl || shield?.imageUrl || "/no-shield.png";

    const content = (
        <div className="flex items-center gap-2">
            <img
                src={imageUrl}
                alt={name}
                className="h-7 w-7 rounded-full border border-zinc-600 object-cover"
            />
            <span>{name}</span>
        </div>
    );

    if (!to) {
        return content;
    }

    return (
        <Link to={to} className="text-brand underline-offset-4 hover:underline">
            {content}
        </Link>
    );
}

function TabButton({
    label,
    isActive,
    onClick,
}: {
    label: string;
    isActive: boolean;
    onClick: () => void;
}) {
    return (
        <button
            onClick={onClick}
            className={`font-garamond rounded-lg border px-3 py-2 text-sm transition sm:text-base ${
                isActive
                    ? "bg-brand border-brand text-zinc-950"
                    : "hover:border-brand border-zinc-700 text-zinc-200"
            }`}
        >
            {label}
        </button>
    );
}

function LeaderboardTable({
    headers,
    rows,
    emptyMessage,
}: {
    headers: string[];
    rows: Array<Array<React.ReactNode>>;
    emptyMessage: string;
}) {
    if (rows.length === 0) {
        return <p className="font-garamond text-zinc-400">{emptyMessage}</p>;
    }

    return (
        <div>
            <div className="space-y-3 md:hidden">
                {rows.map((row, rowIndex) => (
                    <div
                        key={rowIndex}
                        className="rounded-lg border border-zinc-700 bg-zinc-950/70 p-3"
                    >
                        {headers.map((header, headerIndex) => (
                            <div
                                key={`${rowIndex}-${header}`}
                                className="flex items-start justify-between gap-3 py-1"
                            >
                                <span className="font-prviking text-brand text-xs">
                                    {convertLetters(header)}
                                </span>
                                <span className="font-garamond text-right text-sm text-zinc-200">
                                    {row[headerIndex]}
                                </span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-150 border-collapse text-left">
                    <thead>
                        <tr className="border-b border-zinc-700">
                            {headers.map((header) => (
                                <th
                                    key={header}
                                    className="font-prviking text-brand px-2 py-2 text-sm"
                                >
                                    {convertLetters(header)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr
                                key={rowIndex}
                                className="border-b border-zinc-800 last:border-b-0"
                            >
                                {row.map((cell, cellIndex) => (
                                    <td
                                        key={cellIndex}
                                        className="font-garamond px-2 py-2 text-sm text-zinc-200"
                                    >
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
